import { deckSku } from "./entitlements";
import type { UnlockRule } from "./unlocks";
import type { VenueId } from "./venues";

/**
 * A "set" is a premium card deck and the venue built to go with it, sold and
 * unlocked as one thing.
 *
 * WHY A SET EXISTS AT ALL. A player who buys painted cards and is left playing
 * them on the starter table has bought half a theme — the deck was art-directed
 * against a room that isn't there. Pairing them is the product; two pickers that
 * happen to contain matching entries is not, because the player has to find the
 * pairing themselves and most never will. So the set is both an unlock rule and
 * a control on Table & Cards that applies both halves in one tap.
 *
 * THE SKU IS THE DECK'S, ON PURPOSE. The premium entries in `DECK_UNLOCKS` are
 * written by `scripts/deckgen.mjs` inside a generated block, so a separate
 * `set.<id>` SKU would mean either hand-editing generated code or teaching the
 * deck generator about venues. Pointing the venue's unlock rule at the deck's
 * existing SKU buys exactly the same thing — one purchase, two cosmetics — and
 * touches neither. `evaluateUnlock` needed no change to allow it: a venue rule
 * of `{ kind: "purchase" }` already worked, comp included.
 *
 * NOT EVERY SET IS SOLD. Midnight Macabre is given to everybody who plays a
 * round signed in during Halloween 2026 (theme/events.ts), so a set carries a
 * whole unlock RULE rather than a SKU, and both halves share it whatever it is.
 * A set that is not a purchase overrides its deck's generated purchase entry in
 * `DECK_UNLOCKS`.
 *
 * See docs/PREMIUM_VENUES.md.
 */
export interface CosmeticSet {
  id: string;
  /** Shown on the one-tap control. The theme's name, not "the X set". */
  name: string;
  /** One line on what applying it does. */
  blurb: string;
  /** A `CARD_THEMES` id (theme/themes.ts). */
  deckId: string;
  venueId: VenueId;
  /** What opens it. Shared by both halves — that is the whole point — unless `deckUnlock` says otherwise. */
  unlock: UnlockRule;
  /**
   * What opens the DECK, for the one set whose halves are earned separately.
   * Absent, the deck shares `unlock`. The set is only "Use this set" once both
   * halves are held, and its lock names whichever half is still missing.
   */
  deckUnlock?: UnlockRule;
}

export const SETS: CosmeticSet[] = [
  {
    id: "olympian",
    name: "Olympian Pantheon",
    blurb: "Painted gods on a marble table, under a temple's echo",
    deckId: "olympian-pantheon",
    venueId: "olympian",
    unlock: { kind: "purchase", sku: deckSku("olympian-pantheon") },
  },
  {
    id: "biolume",
    name: "Biolume Deep",
    blurb: "Painted light in black water, on a table lit by nothing but the cards",
    deckId: "biolume-deep",
    venueId: "biolume",
    unlock: { kind: "purchase", sku: deckSku("biolume-deep") },
  },
  {
    id: "animal-antics",
    name: "Animal Antics",
    blurb: "Painted pets up to no good, on a wicker table in the afternoon sun",
    deckId: "animal-antics-deck",
    venueId: "porch",
    unlock: { kind: "purchase", sku: deckSku("animal-antics-deck") },
  },
  {
    id: "midnight-macabre",
    name: "Midnight Macabre",
    blurb: "Painted horrors on a graveyard table, with the jack-o'-lanterns lit",
    deckId: "midnight-macabre",
    venueId: "hallowed",
    unlock: { kind: "event", eventId: "halloween-2026" },
  },
  {
    id: "norse-realms",
    name: "Norse Realms",
    blurb: "Painted gods and giants on a carved oak table, by the hearth",
    deckId: "norse-realms",
    venueId: "meadhall",
    unlock: { kind: "purchase", sku: deckSku("norse-realms") },
  },
  /*
   * Not sold, and not earned in one go. The deck is "15 Jackpots", the gold rim
   * of the Jackpot badge: a Five Out on a Bounty Pile, fifteen times. The room
   * is Diamond Sky — Zenith, the campaign's last boss, cleared with all three of
   * its bonus objectives — which is the one cosmetic in the game gated on a
   * badge's diamond tier, by design: it is the room that fight is played in,
   * and it is the prize for beating it completely.
   */
  {
    id: "fractal-aether",
    name: "Fractal Aether",
    blurb: "Painted light at the edge of a black hole, where Zenith is fought",
    deckId: "fractal-aether",
    venueId: "singularity",
    unlock: { kind: "achievement", achievementId: "campaign.zenith.diamond" },
    deckUnlock: { kind: "achievement", achievementId: "bounty.fiveout.15" },
  },
];

/** What opens a set's deck: its own rule if it has one, else the set's. */
export function deckUnlockOf(set: CosmeticSet): UnlockRule {
  return set.deckUnlock ?? set.unlock;
}

/** The set this deck belongs to, if any. */
export function setForDeck(deckId: string): CosmeticSet | undefined {
  return SETS.find((s) => s.deckId === deckId);
}

/** The set this venue belongs to, if any. */
export function setForVenue(venueId: string): CosmeticSet | undefined {
  return SETS.find((s) => s.venueId === venueId);
}

/** True when both halves of the set are currently selected. */
export function isSetApplied(set: CosmeticSet, deckId: string, venueId: string): boolean {
  return set.deckId === deckId && set.venueId === venueId;
}
